import { Inject, Injectable } from '@angular/core';
import { APP_CONFIG, AppConfig } from '@src/app.config';
import { RequestService } from '@shared/services/http/request/request.service';
import { map, Observable } from 'rxjs';
import { SupportCaseMetadataService } from './support-case-metadata.service';

export enum SupportCaseWorkflowState {
  TODO = 0,
  IN_PROGRESS = 1,
  DONE = 2,
  BACKLOG = 3,
}

export const SupportCaseStatus = {
  BACKLOG: 'backlog',
  OPEN: 'open',
  TRIAGED: 'triaged',
  IN_PROGRESS: 'in-progress',
  WAITING_ON_REQUESTER: 'waiting-on-requester',
  RESOLVED: 'resolved',
  CLOSED: 'closed',
} as const;

export type SupportCaseStatus = typeof SupportCaseStatus[keyof typeof SupportCaseStatus];

export enum SupportCaseImpactArea {
  TECHNOLOGY = 1,
  MISSION = 2,
  TREATMENT = 3,
  BUSINESS = 4,
  COMPLIANCE = 5,
}

export enum SupportCaseCategory {
  BUG = 0,
  SUPPORT = 1,
  FEATURE_REQUEST = 2,
  CLINICAL_SUPPORT = 3,
}

export enum SupportCasePriority {
  LOWEST = 0,
  LOW = 1,
  MEDIUM = 2,
  HIGH = 3,
  HIGHEST = 4,
}

export enum SupportCaseSource {
  WEB = 0,
  IOS = 1,
  ANDROID = 2,
  INTERNAL = 3,
  EMAIL = 4,
  PHONE = 5,
  OTHER = 6,
}

export interface SupportCaseDomain {
  id: string;
  name: string;
  description?: string;
  color?: string;
  icon?: string;
}

export interface SupportCaseSprint {
  id: string;
  name: string;
  description?: string;
  startDate: number;
  endDate: number;
  status: 'planning' | 'active' | 'completed';
  startedAt?: number;
  committedCaseIds?: string[];
}

export interface ListSupportCaseDomainsResponse {
  domains: SupportCaseDomain[];
}

export interface ListSupportCaseSprintsResponse {
  sprints: SupportCaseSprint[];
}

export interface SaveSupportCaseDomainRequest {
  id?: string;
  name: string;
  description?: string;
  color?: string;
  icon?: string;
}

export interface SaveSupportCaseSprintRequest {
  id?: string;
  name: string;
  description?: string;
  startDate: number;
  endDate: number;
  status: 'planning' | 'active' | 'completed';
  startedAt?: number;
  committedCaseIds?: string[];
}

export interface SupportCaseAttachment {
  id: string;
  filename: string;
  fileSize: number;
  contentType: string;
  uploadedAt: number;
  uploadedByUserId?: string;
}

export interface SupportCaseAcceptanceCriterion {
  id: string;
  text: string;
  isDone: boolean;
}

export interface SupportCase {
  id: string;
  requesterUserId: string;
  status: SupportCaseStatus;
  state: SupportCaseWorkflowState;
  category: SupportCaseCategory;
  priority: SupportCasePriority;
  source: SupportCaseSource;
  title: string;
  description: string;
  assignedToUserId?: string | null;
  relatedEntityType?: string | null;
  relatedEntityId?: string | null;
  domainId?: string | null;
  sprintId?: string | null;
  impactArea?: SupportCaseImpactArea | null;
  storyPoints?: number | null;
  triageScore?: number | null;
  createdAt: number;
  updatedAt: number;
  deletedAt?: number | null;
  attachments?: SupportCaseAttachment[];
  acceptanceCriteria?: SupportCaseAcceptanceCriterion[];
  metadata: Record<string, string>;
}

export interface SupportCaseComment {
  id: string;
  caseId: string;
  authorUserId: string;
  authorName?: string;
  content: string;
  isInternal: boolean;
  createdAt: number;
}

export interface ListSupportCasesRequestDto {
  status?: SupportCaseStatus;
  state?: SupportCaseWorkflowState;
  category?: SupportCaseCategory;
  priority?: SupportCasePriority;
  source?: SupportCaseSource;
  requesterUserId?: string;
  assignedToUserId?: string;
  domainId?: string;
  sprintId?: string;
  impactArea?: SupportCaseImpactArea;
  pageSize?: number;
  pageToken?: string;
  orderBy?: string;
}

export interface ListSupportCasesResponseDto {
  supportCases: SupportCase[];
  nextPageToken?: string;
}

export interface SupportCaseRegionStatDto {
  region: string;
  count: number;
}

export interface CreateSupportCaseDto {
  requesterUserId: string;
  status?: SupportCaseStatus;
  category?: SupportCaseCategory;
  priority?: SupportCasePriority;
  source?: SupportCaseSource;
  title: string;
  description?: string;
  assignedToUserId?: string;
  relatedEntityType?: string;
  relatedEntityId?: string;
  domainId?: string;
  sprintId?: string;
  impactArea?: SupportCaseImpactArea | null;
  storyPoints?: number | null;
  attachments?: SupportCaseAttachment[];
  acceptanceCriteria?: SupportCaseAcceptanceCriterion[];
  metadata?: Record<string, string>;
}

export interface SubmitFeedbackDto extends CreateSupportCaseDto {}

export interface UpdateSupportCaseDto {
  supportCase: Partial<CreateSupportCaseDto> & { id: string; attachments?: SupportCaseAttachment[] };
  updateMask?: { paths: string[] };
}

export interface SupportCaseStatusDefinition {
  id: string;
  label: string;
  state: SupportCaseWorkflowState;
  sortOrder: number;
  disabled: boolean;
  defaultForCreate: boolean;
}

export interface SupportCaseWorkflowDefinition {
  category: SupportCaseCategory;
  statuses: SupportCaseStatusDefinition[];
}

export interface ListSupportCaseWorkflowsResponse {
  workflows: SupportCaseWorkflowDefinition[];
}

export interface SaveSupportCaseWorkflowRequest {
  category: SupportCaseCategory;
  statuses: SupportCaseStatusDefinition[];
  deletedStatusRemaps: Array<{ fromStatusId: string; toStatusId: string }>;
}

@Injectable({ providedIn: 'root' })
export class SupportCasesApiService {
  private readonly base = `${this.appConfig.angularEndpoint}/supportCases`;

  get angularEndpoint(): string {
    return this.appConfig.angularEndpoint;
  }

  constructor(
    private readonly request: RequestService,
    private readonly supportCaseMetadata: SupportCaseMetadataService,
    @Inject(APP_CONFIG) private readonly appConfig: AppConfig,
  ) {}

  list(q: ListSupportCasesRequestDto): Observable<{ items: SupportCase[]; nextPageToken?: string }> {
    const params = new URLSearchParams();
    if (q.status !== undefined && q.status !== null) params.set('status', String(q.status));
    if (q.state !== undefined && q.state !== null) params.set('state', String(q.state));
    if (q.category !== undefined && q.category !== null) params.set('category', String(q.category));
    if (q.priority !== undefined && q.priority !== null) params.set('priority', String(q.priority));
    if (q.source !== undefined && q.source !== null) params.set('source', String(q.source));
    if (q.requesterUserId) params.set('requesterUserId', q.requesterUserId);
    if (q.assignedToUserId) params.set('assignedToUserId', q.assignedToUserId);
    if (q.domainId) params.set('domainId', q.domainId);
    if (q.sprintId) params.set('sprintId', q.sprintId);
    if (q.impactArea !== undefined && q.impactArea !== null) params.set('impactArea', String(q.impactArea));
    if (q.pageSize) params.set('pageSize', String(q.pageSize));
    if (q.pageToken) params.set('pageToken', q.pageToken);
    if (q.orderBy) params.set('orderBy', q.orderBy);

    const query = params.toString();
    const url = query ? `${this.base}?${query}` : this.base;

    return this.request.get(url).pipe(
      map(response => response.body as ListSupportCasesResponseDto & { items?: any[]; supportCases?: any[] }),
      map(response => ({
        items: (response?.items ?? response?.supportCases ?? []).map(this.mapSupportCase),
        nextPageToken: response?.nextPageToken ?? '',
      }))
    );
  }

  get(id: string): Observable<SupportCase> {
    return this.request.get(`${this.base}/${id}`).pipe(
      map(res => this.mapSupportCase(res.body))
    );
  }

  create(dto: CreateSupportCaseDto): Observable<SupportCase> {
    const payload = this.withMetadata(dto);
    return this.request.post(this.base, payload).pipe(
      map(res => this.mapSupportCase(res.body))
    );
  }

  submitFeedback(dto: SubmitFeedbackDto): Observable<SupportCase> {
    const payload = this.withMetadata(dto);
    return this.request.post(`${this.appConfig.angularEndpoint}/feedback`, payload).pipe(
      map(res => this.mapSupportCase(res.body))
    );
  }

  update(id: string, dto: UpdateSupportCaseDto): Observable<SupportCase> {
    const supportCase = this.withMetadata(dto.supportCase as any);
    return this.request.put(`${this.base}/${id}`, { ...dto, supportCase }).pipe(
      map(res => this.mapSupportCase(res.body))
    );
  }

  delete(id: string): Observable<void> {
    return this.request.delete(`${this.base}/${id}`).pipe(
      map(() => void 0)
    );
  }

  listWorkflows(): Observable<SupportCaseWorkflowDefinition[]> {
    return this.request.get(`${this.base}/workflows`).pipe(
      map(res => (res.body as ListSupportCaseWorkflowsResponse)?.workflows ?? [])
    );
  }

  saveWorkflow(req: SaveSupportCaseWorkflowRequest): Observable<SupportCaseWorkflowDefinition> {
    return this.request.post(`${this.base}/workflows`, req).pipe(
      map(res => res.body as SupportCaseWorkflowDefinition)
    );
  }

  listDomains(): Observable<SupportCaseDomain[]> {
    return this.request.get(`${this.base}/domains`).pipe(
      map(res => (res.body as ListSupportCaseDomainsResponse)?.domains ?? [])
    );
  }

  saveDomain(req: SaveSupportCaseDomainRequest): Observable<SupportCaseDomain> {
    return this.request.post(`${this.base}/domains`, req).pipe(
      map(res => res.body as SupportCaseDomain)
    );
  }

  deleteDomain(id: string): Observable<boolean> {
    return this.request.delete(`${this.base}/domains/${id}`).pipe(
      map(() => true)
    );
  }

  listSprints(): Observable<SupportCaseSprint[]> {
    return this.request.get(`${this.base}/sprints`).pipe(
      map(res => (res.body as ListSupportCaseSprintsResponse)?.sprints ?? [])
    );
  }

  saveSprint(req: SaveSupportCaseSprintRequest): Observable<SupportCaseSprint> {
    return this.request.post(`${this.base}/sprints`, req).pipe(
      map(res => res.body as SupportCaseSprint)
    );
  }

  deleteSprint(id: string): Observable<boolean> {
    return this.request.delete(`${this.base}/sprints/${id}`).pipe(
      map(() => true)
    );
  }

  getRegionStats(): Observable<SupportCaseRegionStatDto[]> {
    return this.request.get(`${this.base}/regionStats`).pipe(
      map(res => (res.body ?? []) as SupportCaseRegionStatDto[])
    );
  }

  getCount(params?: {
    excludeDone?: boolean;
    allRegions?: boolean;
    status?: SupportCaseStatus;
    state?: SupportCaseWorkflowState;
    category?: SupportCaseCategory;
    priority?: SupportCasePriority;
    sprintId?: string;
    domainId?: string;
  }): Observable<number> {
    const sp = new URLSearchParams();
    if (params?.excludeDone !== undefined) sp.set('excludeDone', String(params.excludeDone));
    if (params?.allRegions !== undefined) sp.set('allRegions', String(params.allRegions));
    if (params?.status) sp.set('status', String(params.status));
    if (params?.state !== undefined) sp.set('state', String(params.state));
    if (params?.category !== undefined) sp.set('category', String(params.category));
    if (params?.priority !== undefined) sp.set('priority', String(params.priority));
    if (params?.sprintId) sp.set('sprintId', params.sprintId);
    if (params?.domainId) sp.set('domainId', params.domainId);

    const query = sp.toString();
    const url = query ? `${this.base}/count?${query}` : `${this.base}/count`;
    return this.request.get(url).pipe(
      map(res => (res.body as { count?: number })?.count ?? 0)
    );
  }

  getBacklogCount(allRegions = true): Observable<number> {
    return this.request.get(`${this.base}/backlogCount?allRegions=${allRegions}`).pipe(
      map(res => (res.body as { count?: number })?.count ?? 0)
    );
  }

  private tsToSeconds(value: any): number {
    if (value == null) return 0;
    if (typeof value === 'number') return value > 10_000_000_000 ? Math.floor(value / 1000) : value;
    if (typeof value === 'string') return Math.floor(Date.parse(value) / 1000) || 0;
    if (typeof value === 'object') {
      if (typeof value.seconds === 'number') return value.seconds;
      if (typeof value._seconds === 'number') return value._seconds;
    }
    return 0;
  }

  private withMetadata<T extends CreateSupportCaseDto>(payload: T): T {
    return {
      ...payload,
      metadata: {
        ...(this.supportCaseMetadata.build() ?? {}),
        ...(payload.metadata ?? {}),
      },
    };
  }

  uploadAttachment(caseId: string, file: File): Observable<SupportCaseAttachment> {
    const formData = new FormData();
    formData.append('file', file, file.name);

    return this.request.post(`${this.base}/${caseId}/attachments`, formData).pipe(
      map(r => r.body as SupportCaseAttachment)
    );
  }

  getAttachmentDownloadUrl(caseId: string, attachmentId: string): Observable<{ url: string; filename: string }> {
    return this.request.get(`${this.base}/${caseId}/attachments/${attachmentId}/download`).pipe(
      map(r => (r.body ?? { url: '', filename: '' }) as { url: string; filename: string })
    );
  }

  deleteAttachment(caseId: string, attachmentId: string): Observable<void> {
    return this.request.delete(`${this.base}/${caseId}/attachments/${attachmentId}`).pipe(
      map(() => void 0)
    );
  }

  listComments(caseId: string): Observable<SupportCaseComment[]> {
    return this.request.get(`${this.base}/${caseId}/comments`).pipe(
      map(res => ((res.body ?? []) as any[]).map(c => ({
        id: c?.id ?? '',
        caseId: c?.caseId ?? caseId,
        authorUserId: c?.authorUserId ?? '',
        authorName: c?.authorName ?? '',
        content: c?.content ?? '',
        isInternal: Boolean(c?.isInternal),
        createdAt: this.tsToSeconds(c?.createdAt),
      })))
    );
  }

  addComment(caseId: string, content: string, isInternal: boolean): Observable<SupportCaseComment> {
    return this.request.post(`${this.base}/${caseId}/comments`, { content, isInternal }).pipe(
      map(res => {
        const c = res.body as any;
        return {
          id: c?.id ?? '',
          caseId: c?.caseId ?? caseId,
          authorUserId: c?.authorUserId ?? '',
          authorName: c?.authorName ?? '',
          content: c?.content ?? content,
          isInternal: Boolean(c?.isInternal ?? isInternal),
          createdAt: this.tsToSeconds(c?.createdAt),
        };
      })
    );
  }

  deleteComment(caseId: string, commentId: string): Observable<void> {
    return this.request.delete(`${this.base}/${caseId}/comments/${commentId}`).pipe(
      map(() => void 0)
    );
  }

  private mapSupportCase = (raw: any): SupportCase => ({
    id: raw?.id ?? '',
    requesterUserId: raw?.requesterUserId ?? '',
    status: `${raw?.status ?? SupportCaseStatus.OPEN}` as SupportCaseStatus,
    state: raw?.state ?? SupportCaseWorkflowState.TODO,
    category: raw?.category ?? SupportCaseCategory.SUPPORT,
    priority: raw?.priority ?? SupportCasePriority.MEDIUM,
    source: raw?.source ?? SupportCaseSource.INTERNAL,
    title: raw?.title ?? '',
    description: raw?.description ?? '',
    assignedToUserId: raw?.assignedToUserId ?? null,
    relatedEntityType: raw?.relatedEntityType ?? null,
    relatedEntityId: raw?.relatedEntityId ?? null,
    domainId: raw?.domainId ?? null,
    sprintId: raw?.sprintId ?? null,
    impactArea: raw?.impactArea != null && Number(raw.impactArea) > 0 ? Number(raw.impactArea) as SupportCaseImpactArea : null,
    storyPoints: raw?.storyPoints != null ? Number(raw.storyPoints) : (raw?.StoryPoints != null ? Number(raw.StoryPoints) : null),
    triageScore: raw?.triageScore != null ? Number(raw.triageScore) : (raw?.TriageScore != null ? Number(raw.TriageScore) : null),
    createdAt: this.tsToSeconds(raw?.createdAt),
    updatedAt: this.tsToSeconds(raw?.updatedAt),
    deletedAt: raw?.deletedAt == null ? null : this.tsToSeconds(raw.deletedAt),
    attachments: Array.isArray(raw?.attachments)
      ? raw.attachments.map((a: any) => ({
          id: a?.id ?? a?.Id ?? '',
          filename: a?.filename ?? a?.Filename ?? '',
          fileSize: Number(a?.fileSize ?? a?.FileSize ?? 0),
          contentType: a?.contentType ?? a?.ContentType ?? 'application/octet-stream',
          uploadedAt: this.tsToSeconds(a?.uploadedAt ?? a?.UploadedAt),
          uploadedByUserId: a?.uploadedByUserId ?? a?.UploadedByUserId ?? undefined,
        }))
      : (Array.isArray(raw?.Attachments)
        ? raw.Attachments.map((a: any) => ({
            id: a?.id ?? a?.Id ?? '',
            filename: a?.filename ?? a?.Filename ?? '',
            fileSize: Number(a?.fileSize ?? a?.FileSize ?? 0),
            contentType: a?.contentType ?? a?.ContentType ?? 'application/octet-stream',
            uploadedAt: this.tsToSeconds(a?.uploadedAt ?? a?.UploadedAt),
            uploadedByUserId: a?.uploadedByUserId ?? a?.UploadedByUserId ?? undefined,
          }))
        : []),
    acceptanceCriteria: Array.isArray(raw?.acceptanceCriteria)
      ? raw.acceptanceCriteria.map((c: any) => ({
          id: c?.id ?? c?.Id ?? '',
          text: c?.text ?? c?.Text ?? '',
          isDone: !!(c?.isDone ?? c?.IsDone),
        }))
      : (Array.isArray(raw?.AcceptanceCriteria)
        ? raw.AcceptanceCriteria.map((c: any) => ({
            id: c?.id ?? c?.Id ?? '',
            text: c?.text ?? c?.Text ?? '',
            isDone: !!(c?.isDone ?? c?.IsDone),
          }))
        : []),
    metadata: raw?.metadata ?? raw?.Metadata ?? {},
  });
}
